import { useGame, ITEM_NAMES } from '../state/useGame';
import { getNpc, QUESTS } from '../data';
import type { ItemId } from '../domain/state';
import { canAcceptQuest } from '../domain/quests/eligibility';

export function QuestsPage() {
  const state = useGame((s) => s.state);
  const acceptQuest = useGame((s) => s.acceptQuest);
  const quests = state.quests;

  const active = quests.filter((q) => q.status === 'active');
  const completed = quests.filter((q) => q.status === 'complete');
  const available = QUESTS.quests.filter((q) => !quests.some((qs) => qs.questId === q.id));

  return (
    <>
      <h1 className="page-title">Quests</h1>
      <p className="page-subtitle">
        Crown authority stays human-only. Crown quests come from Mr. Sparks (and the
        occasional Parliament seat). Accepted: {active.length} · Completed: {completed.length}.
      </p>

      <h2 style={{ marginTop: 24, color: 'var(--accent-nous)', fontSize: 16 }}>Available</h2>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
        {available.map((q) => {
          const giver = getNpc(q.giver);
          const check = canAcceptQuest(state, q.id);
          return (
            <div key={q.id} className="card" data-quest-id={q.id} data-quest-status="available">
              <div style={{ fontSize: 12, color: 'var(--ui-text-dim)' }}>
                Tier {q.tier} · {q.kind} · given by {giver.name}
              </div>
              <div style={{ fontSize: 18, fontWeight: 600, color: 'var(--accent-nous)', margin: '4px 0' }}>
                {q.name}
              </div>
              <p style={{ margin: '8px 0', color: 'var(--ui-text-dim)', fontSize: 13, fontStyle: 'italic' }}>
                {q.flavor}
              </p>
              <div style={{ fontSize: 12 }}>
                Objective:{' '}
                {q.objective.type === 'kill' && (
                  <span>
                    Kill <strong>{q.objective.count}</strong> ×{' '}
                    <span style={{ color: 'var(--accent-nous)' }}>{q.objective.monsterId.replaceAll('_', ' ')}</span>
                  </span>
                )}
              </div>
              {(Object.keys(q.reward.xp).length > 0 || q.reward.items.length > 0) && (
                <div style={{ fontSize: 12, marginTop: 6 }}>
                  Reward:{' '}
                  {Object.entries(q.reward.xp).map(([sid, xp]) => (
                    <span key={sid} style={{ color: 'var(--accent-nous)' }}>+{xp} {sid.replaceAll('_', ' ')} XP </span>
                  ))}
                  {q.reward.items.map((itemId) => (
                    <span key={itemId} style={{ color: 'var(--accent-nous)' }}>
                      · {ITEM_NAMES[itemId as ItemId] ?? itemId}{' '}
                    </span>
                  ))}
                </div>
              )}
              {!check.ok && (
                <p className="lock-reason" style={{ fontSize: 12, color: 'var(--warn, #e0a94a)' }}>
                  🔒 {check.reason}
                </p>
              )}
              <div style={{ marginTop: 10 }}>
                <button
                  className="primary"
                  disabled={!check.ok}
                  onClick={() => acceptQuest(q.id)}
                  aria-label={`Accept ${q.name}`}
                >
                  Accept
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {active.length > 0 && (
        <>
          <h2 style={{ marginTop: 24, color: 'var(--pipeline-green)', fontSize: 16 }}>Active</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
            {active.map((qs) => {
              const q = QUESTS.quests.find((x) => x.id === qs.questId);
              if (!q) return null;
              const target = q.objective.type === 'kill' ? q.objective.count : 0;
              return (
                <div
                  key={qs.questId}
                  className="card"
                  style={{ borderColor: 'var(--pipeline-green)' }}
                  data-quest-id={qs.questId}
                  data-quest-status="active"
                  data-progress-current={qs.progress}
                  data-progress-target={target}
                >
                  <div style={{ fontSize: 18, fontWeight: 600, color: 'var(--accent-nous)' }}>{q.name}</div>
                  <div style={{ fontSize: 12, color: 'var(--ui-text-dim)' }}>
                    Progress: {qs.progress} / {target || '?'}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {completed.length > 0 && (
        <>
          <h2 style={{ marginTop: 24, color: 'var(--ui-text-dim)', fontSize: 16 }}>Completed</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
            {completed.map((qs) => {
              const q = QUESTS.quests.find((x) => x.id === qs.questId);
              if (!q) return null;
              return (
                <div
                  key={qs.questId}
                  className="card"
                  data-quest-id={qs.questId}
                  data-quest-status="complete"
                >
                  <div style={{ fontSize: 18, fontWeight: 600, color: 'var(--ui-text-dim)' }}>
                    ✓ {q.name}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--ui-text-dim)' }}>
                    Job table: green. Filed.
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </>
  );
}
